import React from 'react';
import { 
  FaPlus, 
  FaEyeSlash, 
  FaSearch, 
  FaSquare, 
  FaThLarge, 
  FaTh, 
  FaTable, 
  FaThList,
  FaSort,
  FaShare
} from 'react-icons/fa';

const DesktopToolbar = ({
  canEditIdea,
  setShowComponentPicker,
  showSearch,
  setShowSearch,
  isReordering,
  setIsReordering,
  searchQuery,
  setSearchQuery,
  handleSearchChange,
  handleSearchKeyPress,
  handleSearchBlur,
  handleSearchToggle,
  scale,
  setScale,
  filteredComponents,
  components,
  onShareLayout
}) => {
  return (
    <div className="hidden lg:block sticky top-0 z-10 mb-4 transition-all duration-300">
      {/* Background shape that sticks out at the bottom */}
      <div className="absolute -z-10 w-full h-[calc(100%+15px)] bg-[var(--bg-primary)] bottom-[-15px] left-0"></div>
      
      {/* Actual Toolbar with Options */}
      <div className="relative bg-[var(--bg-secondary)] p-3 rounded-lg transition-all duration-300">
        <div className="flex justify-between items-center transition-all duration-300">
          {canEditIdea && (
            <button
              onClick={() => setShowComponentPicker(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg button-magical group magnetic-effect"
            >
              <FaPlus className="transition-transform duration-200 group-hover:rotate-90" /> Add Component
            </button>
          )}
          
          {!canEditIdea && (
            <div className="flex items-center gap-2 px-3 py-2 text-[var(--text-secondary)] text-sm">
              <FaEyeSlash /> Read-only view
            </div>
          )}
          
          <div className="flex items-center space-x-2">
            {/* Share Button */}
            <button
              onClick={onShareLayout}
              className="p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 magnetic-effect text-[var(--secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--secondary)]"
              title="Share project layout"
            >
              <FaShare className="transition-transform duration-200 hover:scale-110" />
            </button>

            {/* Search Section */}
            <div className="flex items-center">
              <div className={`transition-all duration-300 ease-in-out ${
                showSearch && !isReordering ? 'w-48 mr-2' : 'w-0 mr-0'
              }`} style={{ overflow: showSearch && !isReordering ? 'visible' : 'hidden' }}>
                <input
                  id="component-search-input"
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyPress}
                  onBlur={handleSearchBlur}
                  placeholder="Search components..."
                  className="px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent w-48 text-sm"
                />
              </div>
              <button 
                onClick={handleSearchToggle}
                disabled={isReordering}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 magnetic-effect ${
                  isReordering ? 'opacity-50 cursor-not-allowed text-[var(--text-disabled)]' :
                  showSearch ? 'text-[var(--primary)] hover:bg-[var(--bg-hover)]' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                }`}
                title={isReordering ? "Search disabled during reordering" : "Search components"}
              >
                <FaSearch className="transition-transform duration-200 hover:scale-110" />
              </button>
            </div>
            
            {/* Scale Controls */}
            <div className="flex bg-[var(--bg-secondary)] rounded-lg p-1">
              <button
                onClick={() => setScale(1)}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 ${scale === 1 ? 'bg-[var(--primary)] text-white shadow-lg' : 'text-[var(--secondary)] hover:text-white hover:bg-[var(--secondary)]'}`}
                title="Single column"
              >
                <FaSquare className="transform scale-125" />
              </button>
              <button
                onClick={() => setScale(2)}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 ${scale === 2 ? 'bg-[var(--primary)] text-white shadow-lg' : 'text-[var(--secondary)] hover:text-white hover:bg-[var(--secondary)]'} hidden min-[1300px]:block`}
                title="Double columns"
              >
                <FaThLarge className="transform scale-125" />
              </button>
              <button
                onClick={() => setScale(3)}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 ${scale === 3 ? 'bg-[var(--primary)] text-white shadow-lg' : 'text-[var(--secondary)] hover:text-white hover:bg-[var(--secondary)]'} hidden min-[1600px]:block`}
                title="Triple columns"
              >
                <FaThLarge />
              </button>
              <button
                onClick={() => setScale(4)}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 ${scale === 4 ? 'bg-[var(--primary)] text-white shadow-lg' : 'text-[var(--secondary)] hover:text-white hover:bg-[var(--secondary)]'} hidden min-[1900px]:block`}
                title="Quad columns"
              >
                <FaTh />
              </button>
              <button
                onClick={() => setScale(5)}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 ${scale === 5 ? 'bg-[var(--primary)] text-white shadow-lg' : 'text-[var(--secondary)] hover:text-white hover:bg-[var(--secondary)]'} hidden min-[2200px]:block`}
                title="Penta columns"
              >
                <FaTable className="transform scale-110" />
              </button>
              <button
                onClick={() => setScale(6)}
                className={`p-2 rounded transition-all duration-200 hover:scale-110 active:scale-95 ${scale === 6 ? 'bg-[var(--primary)] text-white shadow-lg' : 'text-[var(--secondary)] hover:text-white hover:bg-[var(--secondary)]'} hidden min-[2500px]:block`}
                title="Hexa columns"
              >
                <FaThList className="transform scale-100" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reordering Banner */}
      {isReordering && (
        <div className="mt-4 p-3 bg-[var(--primary)]/10 border border-[var(--primary)]/30 rounded-lg">
          <div className="flex items-center gap-2 text-[var(--primary)]">
            <FaSort className="animate-pulse" />
            <span className="text-sm font-medium">Reordering Mode Active</span>
            <button
              onClick={() => setIsReordering(false)}
              className="ml-auto text-xs px-2 py-1 bg-[var(--primary)] text-white rounded hover:opacity-80 transition-opacity"
            >
              Done
            </button>
          </div>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {components.length > 0 ? 'Drag and drop components to reorder them' : 'Add some components first to enable reordering'}
          </p>
        </div>
      )}
      
      {/* Search Results Info */}
      {searchQuery.trim() && (
        <div className="mt-4 px-4 py-2 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
          <p className="text-sm text-[var(--text-secondary)]">
            {filteredComponents.length === 0 ? 'No components found' : 
             `Found ${filteredComponents.length} component${filteredComponents.length !== 1 ? 's' : ''} matching "${searchQuery}"`}
          </p>
        </div>
      )}
    </div>
  );
};

export default DesktopToolbar;
